import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { ChannelCard } from './channel-card';
import { useChannelDescription } from './use-channel-description';

export function Channels() {
  const { selectedOpeningIds } = useJobPostingContext();
  const { getDescription } = useChannelDescription();

  const {
    data: { channels, indeed_integration_enabled: indeedIntegrationEnabled },
  } = useSuspenseQuery({
    queryKey: ['channels', selectedOpeningIds],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        selectedOpeningIds,
      ),
  });

  if (channels.length === 0) {
    return (
      <div className="flex w-full flex-col items-start justify-center gap-4 p-4">
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="Looks like there are no publishable channels configured for this account at this time."
            description="Message to show when there are no publishable channels configured for the account"
          />
        </Typography>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-4">
      {channels
        .filter(channel => !!getDescription(channel.slug)) // Only show supported channels
        .map(channel => (
          <ChannelCard
            key={channel.slug}
            channel={channel}
            indeedIntegrationEnabled={indeedIntegrationEnabled}
          />
        ))}
    </div>
  );
}

Channels.Skeleton = function ChannelsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-4">
      {_range(4).map(index => (
        <div
          key={index}
          className="border-fx-neutral-200 w-90 flex items-center gap-4 rounded-lg border p-4"
        >
          <Checkbox checked={false} disabled />
          <div className="flex w-full flex-col items-start justify-center gap-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-5 w-40" />
            <Skeleton className="-mt-2 h-4 w-full" />
            <Skeleton className="-mt-3 h-4 w-[90%]" />
          </div>
        </div>
      ))}
    </div>
  );
};
